import { useCallback, useEffect, useMemo } from 'react'
import { Params, useQueryWithParams } from 'hooks/useQueryWithParams'
import { OrderDirection, SearchTypeEnum } from '__generatedTypes__/globalTypes'
import { FIND_ALL_SUCCESS_STORY_ARTICLES } from 'queries/successStory'
import { getWithSectionFilter } from '../../BlogPostList/helpers'
import { FindAllSuccessStoryArticles, FindAllSuccessStoryArticlesVariables } from '../types'

type SuccessStoriesArticlesQueryFilters = {
  platformId: string
  statusId: string
}

type UseSuccessStoriesArticlesQueryOptions = {
  page: number
  take: number
  search: string
  platformId: string
  statusId: string
  onChange: (params: SuccessStoriesArticlesQueryParams) => void
}

export type SuccessStoriesArticlesQueryParams = Params<SuccessStoriesArticlesQueryFilters>

const castSuccessStoriesArticlesParams = ({
  page,
  take,
  search,
  filters,
}: SuccessStoriesArticlesQueryParams): FindAllSuccessStoryArticlesVariables => {
  const withSection = filters.statusId ? getWithSectionFilter(filters.statusId) : undefined
  const platformId = filters.platformId ? { id: filters.platformId } : undefined
  const querySearch = search ? { query: search, type: SearchTypeEnum.ILike } : undefined
  const skip = (page - 1) * take

  return {
    query: {
      skip,
      take,
      relations: ['section'],
      search: querySearch,
      where: {
        platform: platformId,
        withSection,
      },
      order: {
        updatedAt: OrderDirection.DESC,
      },
    },
  }
}

export const useSuccessStoriesArticlesQuery = ({
  page,
  take,
  search,
  platformId,
  statusId,
  onChange,
}: UseSuccessStoriesArticlesQueryOptions) => {
  const queryParams = useMemo(
    () => ({
      page,
      take,
      search,
      filters: {
        platformId,
        statusId,
      },
    }),
    [page, platformId, search, statusId, take]
  )

  const articlesQuery = useQueryWithParams<
    FindAllSuccessStoryArticles,
    FindAllSuccessStoryArticlesVariables,
    'findAllSuccessStory',
    'items',
    'count',
    SuccessStoriesArticlesQueryFilters
  >(
    FIND_ALL_SUCCESS_STORY_ARTICLES,
    'findAllSuccessStory',
    'items',
    'count',
    castSuccessStoriesArticlesParams,
    onChange,
    {
      fetchPolicy: 'network-only',
    }
  )

  useEffect(() => {
    void articlesQuery.refetch(queryParams)
  }, [articlesQuery.refetch, queryParams])

  const refetchCurrent = useCallback(() => articlesQuery.refetch(queryParams), [articlesQuery.refetch, queryParams])

  return {
    ...articlesQuery,
    refetchCurrent,
  }
}
